import {
  AssetFrontendCmpFormApi,
  AssetFrontendCmpFormApi$FormField,
  AssetFrontendCmpFormApi$FieldType,
  AssetFrontendCmpFormApi$FormInput,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import React, { useMemo } from "react";
import useUiContext from "@/custolink-hooks/useUiContext";
import useFormServerBacked from "@/custolink-hooks/form/useFormServerBacked";
import toast from "@/components/ui/toast";
import Notification from "@/components/ui/Notification";
import { rdaNotify } from "@/custolink-views/asset_frontend/redesigns/RdaToast";
import { Controller, useFieldArray } from "react-hook-form";
import PhoneNumberFormItemsControlled from "@/components/form/PhoneNumberFormItemsControlled";
import CountrySelectionControlled from "@/components/form/CountrySelectionControlled";
import { useTranslation } from "react-i18next";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import {
  AssetViewDetails,
} from "@/custolink-views/asset_frontend/asset_view/useAssetViewFacade";
import {
  ErrorNote,
  FormField,
  InfoNote,
  PrimaryButton,
  RadioField,
  SelectField,
  TextField,
} from "@/custolink-views/asset_frontend/redesigns/formKit";

// Shared with the classic design (RegisterProduct/Inquiry render this same component under
// AssetViewClassic), so the submit-success toast follows the asset-frontend switch; the rest
// of this file was already redesigned unconditionally by an earlier batch.
export default function CustomfieldsForm(p: {
  assetViewDetails: AssetViewDetails;
  componentId: number;
  fields: Array<AssetFrontendCmpFormApi$FormField>;
  topAlert: string;
  submitText: string;
  onDone: () => void;
  i18nprefix: string;
}) {
  const defValues = useMemo(() => {
    const blankArray: Array<{ value: "" }> = new Array(p.fields.length).fill({
      value: "",
    });
    return blankArray;
  }, [p.fields]);

  const uic = useUiContext();
  const {t} = useTranslation()
  const newDesign = useAssetFrontendDesign();
  const form = useFormServerBacked<AssetFrontendCmpFormApi$FormInput>(
    {
      values: defValues,
    },
    (d) => {
      return AssetFrontendCmpFormApi.submitCustomfieldsForAssetView(
        uic._orgId,
        p.componentId,
        p.assetViewDetails.urlParamsForRequests.viewId,
        p.assetViewDetails.urlParamsForRequests.viewValue,
        p.assetViewDetails.urlParamsForRequests.assetId,
        d,
      );
    },
    () => {
      const message = t('component.form.submit.thankYou');
      if (newDesign) {
        rdaNotify.success(message);
      } else {
        toast.push(<Notification title={message} type="success" />, { placement: "top-center" });
      }
      p.onDone();
    },
    undefined,
    "asset",
  );

  const { fields } = useFieldArray({
    control: form.control,
    name: "values",
  });

  return (
    <div className="space-y-3">
      <InfoNote>{p.topAlert}</InfoNote>

      <form onSubmit={form.handleSubmit()}>

        {fields.map((field, idx) => {
          const label = p.fields[idx].i18nNameKey != null ? t(p.i18nprefix+"."+p.fields[idx].i18nNameKey) : p.fields[idx].name;
          const error = form.errors[`fieldvalues.${idx}.value`] as string | undefined;
          if (
            p.fields[idx].fieldType ==
            AssetFrontendCmpFormApi$FieldType.PhoneNumber
          ) {
            return (
              <Controller
                key={field.id}
                name={`values.${idx}.value`}
                control={form.control}
                render={(renderProps) => {
                  return (
                    <PhoneNumberFormItemsControlled
                      size={"sm"}
                      field={renderProps.field}
                      label={label}
                      required={p.fields[idx].required}
                      invalid={form.errors[`fieldvalues.${idx}.value`] != null}
                      errorMessage={form.errors[`fieldvalues.${idx}.value`]}
                      countryPlaceholder={t('component.form.country')}
                      numberPlaceholder={t('component.form.number')}
                    />
                  );
                }}
              />
            );
          }

          return (
            <FormField
              key={field.id}
              label={label}
              required={p.fields[idx].required}
              error={error}
            >
              <Controller
                name={`values.${idx}.value`}
                control={form.control}
                render={({ field }) => {
                  if (
                    p.fields[idx].fieldType ==
                    AssetFrontendCmpFormApi$FieldType.CountrySelect
                  ) {
                    return (
                      <CountrySelectionControlled
                        field={field}
                        appendNameToReturnValue={true}
                      />
                    );
                  } else if (
                    p.fields[idx].fieldType ==
                    AssetFrontendCmpFormApi$FieldType.NumberField
                  ) {
                    return (
                      <TextField
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder={t('component.form.number')}
                        {...field}
                      />
                    );
                  } else if (
                    p.fields[idx].fieldType ==
                    AssetFrontendCmpFormApi$FieldType.EmailField
                  ) {
                    return (
                      <TextField
                        type="email"
                        autoComplete="off"
                        placeholder={t('component.form.email')}
                        {...field}
                      />
                    );
                  } else if (
                    p.fields[idx].fieldType ==
                    AssetFrontendCmpFormApi$FieldType.DropdownSelection
                  ) {
                    return (
                      <SelectField
                        ref={field.ref}
                        placeholder={t('component.form.choose')}
                        value={field.value ?? ""}
                        options={p.fields[idx].selectionOptions}
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    );
                  } else if (
                    p.fields[idx].fieldType ==
                    AssetFrontendCmpFormApi$FieldType.RadioSelection
                  ) {
                    return (
                      <RadioField
                        value={field.value}
                        options={p.fields[idx].selectionOptions}
                        onChange={field.onChange}
                      />
                    );
                  } else {
                    return (
                      <TextField
                        type="text"
                        autoComplete="off"
                        {...field}
                      />
                    );
                  }
                }}
              />
            </FormField>
          );
        })}

        {Object.keys(form.errors).length > 0 && (
          <div className="pt-2">
            <ErrorNote>{t('component.form.alert.someErrorsInForm')}</ErrorNote>
          </div>
        )}

        <div className="mt-5">
          <PrimaryButton type="submit">{p.submitText}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}
